import moment from 'moment'
import Posts from '../../mixins/Posts'
import Pages from '../../mixins/Pages'
import User from '../../mixins/User'
import Moderation from '../../mixins/Moderation'
import Insights from '../../mixins/Insights'
import Popup from '@/utils/Popup'
import { isReportingEligible } from '@/utils/reportingEligibility'
import { hasBetaFeature } from '@/utils/betaFeatures'
import { readPeriod, writePeriod, readSection, writeSection, periodCacheKey } from './cache'
import { DEFAULT_PERIOD, periodRange } from './periods'
import { INSIGHTS_NETWORKS, insightsRequest, normalizeInsights } from './postInsights'
import { shortLinksAvailable, fetchShortLinkMetrics } from '@/utils/shortLinkMetrics'

const REFRESH_AFTER_REPORTING = 'overview/refreshAfterReporting'

const { VUE_APP_API_HOST } = process.env

/**
 * Chargements partagés par l'overview général et les tableaux de bord personnalisés.
 * Le composant fournit `period` (data) ; il peut restreindre les pages via `scopePageIds` (computed, null = toutes)
 * et isoler son cache local via `cacheScope` (computed, '' = overview général).
 */
export default {
  mixins: [Posts, Pages, Moderation, Insights, User],

  data() {
    return {
      alive: true,
      periodWasStored: true,

      pagesData: [],
      overview: null,
      overviewLoading: false,
      overviewError: false,
      overviewRequestId: 0,
      // Clics des liens courts mws.li sur la période (tuile de l'overview ; null = indisponible)
      shortLinks: null,
      // Comparaisons (période précédente) en cours de chargement, après l'affichage de la période courante
      overviewPreviousLoading: false,
      // Dernières publications chargées à part (rapides), sans attendre les stats de l'overview
      lastPosts: null,
      lastPostsLoading: false,
      lastPostStats: {},

      upcoming: [],
      upcomingLoading: false,
      // Listes « À traiter » (items au format de toPostItem, ouverts via le lien profond Posts2)
      riskCandidates: [],
      validationPosts: [],
      assignedPosts: [],
      inboxCount: 0,
      inboxCounts: {},
      // Derniers événements Inbox en attente, chargés à l'ouverture du pop-in (20 max)
      inboxEvents: null,
      inboxEventsLoading: false,
      inboxEventsFresh: false,
      // Alertes calculées au moins une fois (cache ou réseau) : évite d'afficher puis masquer le bandeau
      sideLoaded: false
    }
  },

  beforeDestroy() {
    this.alive = false
  },

  computed: {
    currentUser() {
      try {
        return localStorage.user && localStorage.user != 'undefined' ? JSON.parse(localStorage.user) : null
      } catch (e) {
        return null
      }
    },

    getUserRole() {
      const user = this.currentUser
      if (!user) return null
      if (user.admin)
        return 'Admin'
      return user.role && user.role.length ? user.role[0] : 'Editor'
    },

    canEdit() {
      return ['Admin', 'Editor'].includes(this.getUserRole)
    },

    isInternal() {
      return !!(this.currentUser && this.currentUser.email && this.currentUser.email.includes('@mashup-web.com'))
    },

    // Même règle que l'onglet Gestion de présence (Tabs.vue isPresenceManagementEnabled + rôle + plan)
    presenceEnabled() {
      if (!['Admin', 'Editor'].includes(this.getUserRole) || !this.getUserPlan) return false
      if (process.env.NODE_ENV !== 'production') return true
      const user = this.currentUser || {}
      if (user.email && user.email.toLowerCase().endsWith('@mashup-web.com')) return true
      const authorizedAccounts = ['9e046e84-5b26-4c94-8379-9a88ea278490', '6a184790-5f10-43eb-8f16-05daa21d6dc5']
      return authorizedAccounts.includes(user.account) || user.id === '9e046e84-5b26-4c94-8379-9a88ea278490' || hasBetaFeature('presence_management', user)
    },

    reportingAllowed() {
      return this.checkLock('reporting') || this.isInternal || this.forced('forceReporting')
    },

    inboxAllowed() {
      return this.checkLock('moderation') || this.isInternal || this.forced('forceModeration')
    },

    // Pages restreintes (tableau de bord personnalisé) ; null = toutes les pages accessibles
    scopePageIds() {
      return null
    },

    cacheScope() {
      return ''
    },

    scopedPages() {
      if (!this.scopePageIds) return this.pagesData
      const ids = new Set(this.scopePageIds.map(String))
      return this.pagesData.filter(p => ids.has(String(p.id)))
    },

    lastPostsView() {
      return this.lastPosts || (this.overview && this.overview.last_posts) || null
    },

    // À venir enrichi du risque d'échec (page déconnectée, jeton qui expire avant la date)
    upcomingView() {
      return this.upcoming.map(item => ({ ...item, risk: this.riskReason(item) }))
    },

    // Publications planifiées à venir (toutes dates) qui risquent de ne pas partir, les plus proches d'abord
    riskPosts() {
      return this.riskCandidates
        .map(item => ({ ...item, page: this.pagesData.find(p => p.id === item.page_id) || item.page, risk: this.riskReason(item) }))
        .filter(item => item.risk)
        .sort((a, b) => (a.date < b.date ? -1 : 1))
    },

    // id de page -> prochaine publication planifiée ou brouillon daté (liste « À venir », déjà triée)
    nextScheduledByPage() {
      return this.upcoming.reduce((acc, item) => {
        if (item.page_id && !acc[item.page_id]) acc[item.page_id] = item.date
        return acc
      }, {})
    }
  },

  methods: {
    forced(key) {
      try {
        return localStorage[key] ? JSON.parse(localStorage[key]) == true : false
      } catch (e) {
        return false
      }
    },

    cacheUserId() {
      return this.currentUser && this.currentUser.id
    },

    // Clé du cache local : utilisateur, + tableau de bord éventuel
    cacheKey() {
      const userId = this.cacheUserId()
      return userId && this.cacheScope ? `${userId}:${this.cacheScope}` : userId
    },

    // Dernières données connues affichées immédiatement, remplacées à l'arrivée des données fraîches
    hydrateFromCache() {
      const key = this.cacheKey()
      if (!this.pagesData.length) this.pagesData = this.cachedPages()
      const cached = readPeriod(key, this.period)
      if (cached) this.overview = cached
      this.lastPosts = this.cachedLastPosts()

      const withPage = list => (list || []).map(item => ({ ...item, page: this.pagesData.find(p => p.id === item.page_id) || this.cachedPage(item.page_id) }))
      const side = readSection(key, 'side')
      if (side) {
        this.upcoming = withPage(side.upcoming)
        this.riskCandidates = withPage(side.riskCandidates)
        this.validationPosts = withPage(side.validationPosts).filter(this.isNotPast)
        this.assignedPosts = withPage(side.assignedPosts).filter(this.isNotPast)
        this.sideLoaded = true
      }

      const inbox = readSection(key, 'inbox')
      if (inbox) {
        this.inboxCount = inbox.count || 0
        this.inboxCounts = inbox.counts || {}
      }

      // Derniers événements Inbox : affichés depuis le cache, rafraîchis à l'ouverture du pop-in
      const inboxEvents = readSection(key, 'inboxEvents')
      if (inboxEvents) this.inboxEvents = withPage(inboxEvents)

      const stats = readSection(key, 'stats')
      if (stats) this.lastPostStats = Object.fromEntries(Object.entries(stats).map(([k, metrics]) => [k, { loading: false, metrics }]))
    },

    // Après une reconnexion native : pages (story_config) et états de connexion de l'engine rafraîchis
    async onNativeReconnected() {
      await this.loadPages()
      if (this.alive) this.loadOverview(true)
    },

    cachedPages() {
      try {
        return localStorage.pages && localStorage.pages != 'undefined' ? JSON.parse(localStorage.pages) : []
      } catch (e) {
        return []
      }
    },

    cachedPage(pageId) {
      return this.cachedPages().find(p => p.id === pageId) || null
    },

    /**
     * Pages accessibles avec leur état de connexion (bot_status)
     */
    async loadPages() {
      this.pagesData = this.cachedPages()
      try {
        const res = await this.getPages()
        if (!this.alive) return
        const pages = res && res.body && res.body.pages
        if (Array.isArray(pages)) {
          this.pagesData = pages
          localStorage.setItem('pages', JSON.stringify(pages))
        }
      } catch (e) {
        console.error('[overview] pages', e)
      }
    },

    async loadOverview(refresh = false) {
      const requestId = ++this.overviewRequestId
      const { since, until } = periodRange(this.period)
      const params = new URLSearchParams()
      if (since !== null) {
        params.set('since', since)
        params.set('until', until)
      }
      params.set('timezone', new Date().getTimezoneOffset())
      if (this.scopePageIds) params.set('pages', this.scopePageIds.join(','))
      if (refresh) params.set('refresh', '1')

      this.overviewLoading = true
      this.overviewError = false
      this.loadLastPosts(requestId, params)
      this.loadShortLinks(requestId, { since, until }, refresh)
      try {
        // Sans la période précédente : affichage dès la période courante, comparaisons chargées ensuite
        const res = await this.$http.get(`${VUE_APP_API_HOST}/reporting/overview?${params.toString()}&previous=0`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        if (!this.alive || requestId !== this.overviewRequestId) return
        this.overview = this.keepComparisons(res.body, this.overview)
        writePeriod(this.cacheKey(), this.period, this.overview)
        if (this.fallbackToAllTimeIfIdle()) return
        if (res.body.previous_pending) this.loadOverviewPrevious(requestId, params)
      } catch (e) {
        if (!this.alive || requestId !== this.overviewRequestId) return
        console.error('[overview] reporting', e)
        this.overviewError = true
      } finally {
        if (this.alive && requestId === this.overviewRequestId) this.overviewLoading = false
      }
    },

    // Clics des liens courts (tuile dédiée), en parallèle des KPI ; une erreur masque simplement la tuile
    async loadShortLinks(requestId, range, refresh = false) {
      // Overview général uniquement : les tableaux de bord personnalisés passent par le widget short_links
      if (this.cacheScope || !shortLinksAvailable()) return
      try {
        const data = await fetchShortLinkMetrics(range, this.scopePageIds || [], { refresh })
        if (!this.alive || requestId !== this.overviewRequestId) return
        this.shortLinks = data
      } catch (e) {
        if (this.alive && requestId === this.overviewRequestId) this.shortLinks = null
      }
    },

    // Comparaisons (période précédente) ajoutées à l'overview déjà affiché
    async loadOverviewPrevious(requestId, params) {
      this.overviewPreviousLoading = true
      try {
        const res = await this.$http.get(`${VUE_APP_API_HOST}/reporting/overview/previous?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        if (!this.alive || requestId !== this.overviewRequestId || !this.overview) return
        this.overview = this.withComparisons(this.overview, res.body)
        writePeriod(this.cacheKey(), this.period, this.overview)
      } catch (e) {
        console.error('[overview] comparaisons', e)
      } finally {
        if (this.alive && requestId === this.overviewRequestId) this.overviewPreviousLoading = false
      }
    },

    /**
     * Overview complété des comparaisons : totaux précédents, publications précédentes, évolution des abonnés par page
     * @param {Object} comparisons - { previous, previous_posted, fans_delta: { pageId: delta } }
     */
    withComparisons(overview, comparisons) {
      const fans = (comparisons && comparisons.fans_delta) || null
      return {
        ...overview,
        previous: comparisons ? comparisons.previous : null,
        previous_pending: false,
        publishing: overview.publishing ? { ...overview.publishing, previous_posted: comparisons ? comparisons.previous_posted : null } : overview.publishing,
        per_page: (overview.per_page || []).map(p => ({ ...p, fans_delta: fans && Object.prototype.hasOwnProperty.call(fans, String(p.id)) ? fans[String(p.id)] : p.fans_delta }))
      }
    },

    // En attendant les nouvelles comparaisons, celles déjà affichées pour la même période restent visibles
    keepComparisons(fresh, current) {
      if (!fresh || !fresh.previous_pending || !current || !current.previous || !current.period || !fresh.period) return fresh
      const samePeriod = current.period.all_time === fresh.period.all_time &&
        Math.abs((current.period.since || 0) - (fresh.period.since || 0)) < 2 * 86400 &&
        Math.abs((current.period.until || 0) - (fresh.period.until || 0)) < 2 * 86400
      if (!samePeriod) return fresh
      const fans = {}
      ;(current.per_page || []).forEach(p => { fans[String(p.id)] = p.fans_delta })
      return { ...this.withComparisons(fresh, { previous: current.previous, previous_posted: current.publishing && current.publishing.previous_posted, fans_delta: fans }), previous_pending: true }
    },

    // Compte peu actif : sans choix mémorisé et sans post sur 30 jours, on affiche « depuis le début »
    fallbackToAllTimeIfIdle() {
      if (this.periodWasStored || this.period.key !== DEFAULT_PERIOD.key) return false
      const pub = this.overview && this.overview.publishing
      if (!pub || pub.posted > 0) return false
      this.periodWasStored = true
      this.period = { key: 'all' }
      this.loadOverview()
      return true
    },

    cachedLastPosts() {
      const entry = readSection(this.cacheKey(), 'lastPosts')
      return entry && entry.period === periodCacheKey(this.period) ? entry.data : null
    },

    /**
     * Dernières publications (endpoint léger), puis leurs stats en direct
     */
    async loadLastPosts(requestId, params) {
      this.lastPosts = this.cachedLastPosts()
      this.lastPostsLoading = true
      try {
        const res = await this.$http.get(`${VUE_APP_API_HOST}/reporting/overview/last-posts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        if (!this.alive || requestId !== this.overviewRequestId) return
        this.lastPosts = res.body
        writeSection(this.cacheKey(), 'lastPosts', { period: periodCacheKey(this.period), data: res.body })
      } catch (e) {
        // Repli : last_posts de la réponse complète de l'overview
        if (!this.alive || requestId !== this.overviewRequestId) return
        console.error('[overview] last posts', e)
        this.lastPosts = null
      } finally {
        if (this.alive && requestId === this.overviewRequestId) this.lastPostsLoading = false
      }
      if (this.lastPosts) this.loadLastPostStats(requestId)
    },

    /**
     * Stats en direct des dernières publications (POST /insights), une à une comme l'ancien overview
     */
    async loadLastPostStats(requestId) {
      const posts = (this.lastPostsView && this.lastPostsView.posts) || []
      // Stats déjà connues (cache) gardées à l'écran pendant leur rafraîchissement
      const previous = this.lastPostStats || {}
      const stats = {}
      posts.forEach(post => {
        const key = this.postKey(post)
        stats[key] = this.canFetchInsights(post) ? { loading: true, metrics: previous[key] ? previous[key].metrics : null } : null
      })
      this.lastPostStats = stats

      for (const post of posts) {
        if (!this.canFetchInsights(post)) continue
        const key = this.postKey(post)
        let metrics = null
        try {
          const res = await this.getInsights(insightsRequest(post))
          const embed = res && res.body && res.body.posts && res.body.posts[0] && res.body.posts[0].page && res.body.posts[0].page[0]
          metrics = normalizeInsights(post.network, embed, (k) => this.$t(k))
        } catch (e) {
          metrics = null
        }
        if (!this.alive || requestId !== this.overviewRequestId) return
        this.$set(this.lastPostStats, key, { loading: false, metrics })
      }
      const persisted = Object.fromEntries(Object.entries(this.lastPostStats).filter(([, s]) => s && s.metrics).map(([k, s]) => [k, s.metrics]))
      writeSection(this.cacheKey(), 'stats', persisted)
    },

    postKey(post) {
      return `${post.news_id}-${post.bot_uid || post.page_id}`
    },

    // Groupes Facebook / Instagram exclus, comme dans l'ancien overview
    canFetchInsights(post) {
      if (!post.bot_uid || !post.network_post_id || !INSIGHTS_NETWORKS.includes(post.network)) return false
      const page = this.pagesData.find(p => p.id === post.page_id)
      return !(page && page.isGroup)
    },

    async loadPostsAlerts() {
      const pageIds = this.scopedPages.map(p => p.id).filter(Boolean)
      if (!pageIds.length) {
        this.sideLoaded = true
        return
      }
      const now = moment()

      this.upcomingLoading = true
      const until = now.clone().add(7, 'days')
      // Pages pouvant faire échouer une publication planifiée : sans API (déconnectée / natif seul) ou jeton qui expire
      const riskPageIds = this.scopedPages
        .filter(p => [0, 2].includes(Number(p.bot_status)) || (p.story_config && p.story_config.onlyConciergerie))
        .map(p => p.id)
      // Posts planifiés et brouillons datés des 7 prochains jours (l'API arrondit endDate à la fin de journée)
      const scheduledPromise = this.fetchAllPosts(pageIds, { statuses: 'scheduled,draft', startDate: now.toDate(), endDate: until.toDate() })
        .then(scheduled => {
          if (!this.alive) return scheduled
          this.upcoming = scheduled
            .filter(post => this.isUpcomingCandidate(post))
            .map(post => this.toUpcomingItem(post))
            .filter(item => item && moment(item.date).isAfter(now) && !moment(item.date).isAfter(until))
            .sort((a, b) => (a.date < b.date ? -1 : 1))
          this.upcomingLoading = false
          return scheduled
        })
      const [scheduled, drafts, riskScheduled] = await Promise.all([
        scheduledPromise,
        this.canEdit ? this.fetchAllPosts(pageIds, { statuses: 'draft' }) : [],
        riskPageIds.length ? this.fetchAllPosts(riskPageIds, { statuses: 'scheduled', startDate: now.toDate() }) : []
      ])
      if (!this.alive) return

      const isScheduledPost = post => this.isUpcomingCandidate(post) && !(post.status === 'draft' || (post.page && post.page[0] && post.page[0].isDraft))
      const dedupe = items => Object.values(items.reduce((acc, item) => ({ ...acc, [item.key]: item }), {}))
      // Publications planifiées (statut 1, hors brouillons) : pages à risque (toutes dates) + planifiées non validées (7 jours)
      this.riskCandidates = dedupe([
        ...riskScheduled.filter(isScheduledPost).map(post => this.toPostItem(post)),
        ...scheduled.filter(isScheduledPost).map(post => this.toPostItem(post)).filter(item => item && item.validation_pending)
      ].filter(item => item && item.date && moment(item.date).isAfter(now)))

      const userId = this.currentUser && this.currentUser.id
      const embedOf = d => (d.page && d.page[0]) || {}
      const byDate = (a, b) => (a.date || '9999') < (b.date || '9999') ? -1 : 1
      // En attente de validation : validation requise et pas encore validée (brouillons + planifiées des 7 jours)
      this.validationPosts = dedupe([...drafts, ...scheduled.filter(isScheduledPost)]
        .filter(d => this.isValidationPending(embedOf(d)))
        .map(d => this.toPostItem(d))
        .filter(item => item && this.isNotPast(item))).sort(byDate)
      this.assignedPosts = drafts.filter(d => {
        const affected = embedOf(d).affectedTo || {}
        const users = affected.users || (affected.user ? [affected.user] : [])
        return users.some(u => u && (u.id || u) === userId)
      }).map(d => this.toPostItem(d)).filter(item => item && this.isNotPast(item)).sort(byDate)
      this.sideLoaded = true

      // Pages non stockées dans le cache (déjà dans localStorage.pages), retrouvées par page_id à l'hydratation
      writeSection(this.cacheKey(), 'side', {
        upcoming: this.upcoming.map(({ page, ...item }) => item),
        riskCandidates: this.riskCandidates.map(({ page, ...item }) => item),
        validationPosts: this.validationPosts.map(({ page, ...item }) => item),
        assignedPosts: this.assignedPosts.map(({ page, ...item }) => item)
      })
    },

    // À valider / assignées : publications à venir seulement (les brouillons sans date restent, ils ne sont pas passés)
    isNotPast(item) {
      return !item.date || moment(item.date).isAfter(moment())
    },

    /**
     * Toutes les pages de résultats de /posts/v2 (curseur), avec un garde-fou sur le nombre d'appels
     */
    async fetchAllPosts(pageIds, options) {
      const all = []
      let cursor = null
      for (let i = 0; i < 25; i++) {
        const res = await this.postsByPagesV2(pageIds, { ...options, cursor, limit: 200 })
        // postsByPagesV2 renvoie aussi les erreurs HTTP : sans ce log, un échec affichait juste un bloc vide
        if (!res || !res.ok) console.error('[overview] posts v2', res && res.status)
        const body = (res && res.body) || {}
        if (Array.isArray(body.posts)) all.push(...body.posts)
        if (!body.hasMore || !body.nextCursor || !this.alive) break
        cursor = body.nextCursor
      }
      return all
    },

    // Planifié = statut 1 (le 7 « en cours de création » est exclu) ; brouillon = draft PG ou Mongo, hors supprimés
    isUpcomingCandidate(post) {
      const embed = post.page && post.page[0]
      if (!embed) return false
      if (post.status === 'draft' || embed.isDraft) return Number(embed.status) !== -1
      return Number(embed.status) === 1
    },

    toUpcomingItem(post) {
      const item = this.toPostItem(post)
      return item && item.date ? item : null
    },

    // Item de publication commun (À venir, À traiter), avec les infos du lien profond Posts2 ; date facultative (brouillons)
    toPostItem(post) {
      const embed = post.page && post.page[0]
      if (!embed) return null
      const date = embed.scheduled_date || post.scheduled_date || null
      const bot = (post._bots || []).find(b => b.uid === embed.id) || (post._bots || [])[0]
      const pageId = bot && (bot.page && bot.page.id ? bot.page.id : bot.page)
      const page = this.pagesData.find(p => p.id === pageId) || null
      const text = embed.description || post.title || post.description || ''
      return {
        key: `${post._id || post.id}-${embed.id}`,
        // Lien profond Posts2 (cf. openPublishedPost)
        news_id: post._id || post.id,
        is_draft: post.status === 'draft' || !!embed.isDraft,
        page_id: pageId,
        bot_id: bot && bot.id,
        date,
        page,
        text: String(text).slice(0, 160),
        image: embed.enclosure || post.enclosure || null,
        validation_pending: this.isValidationPending(embed)
      }
    },

    loadInboxCount() {
      const user = this.currentUser
      if (!user || !this.inboxAllowed) return
      this.countWaitingEvents(user.account).then(res => {
        if (!this.alive || !res || !res.body || !res.body.pages) return
        const allowed = p => ((!user.pages_access || !user.pages_access.length) && user.admin) || (user.pages_access && user.pages_access.includes(p.id))
        const inScope = p => !this.scopePageIds || this.scopePageIds.map(String).includes(String(p.id))
        const pages = res.body.pages.filter(p => allowed(p) && inScope(p))
        this.inboxCount = pages.reduce((s, p) => s + (p.count || 0), 0)
        this.inboxCounts = pages.reduce((acc, p) => ({ ...acc, [p.id]: p.count || 0 }), {})
        writeSection(this.cacheKey(), 'inbox', { count: this.inboxCount, counts: this.inboxCounts })
      })
    },

    // Raison pour laquelle une publication planifiée risque de ne pas partir (null si aucune)
    riskReason(item) {
      if (!item || item.is_draft) return null
      const page = item.page || this.pagesData.find(p => p.id === item.page_id)
      if (!page) return null
      if (this.isPageUnusable(page)) return 'disconnected'
      if (Number(page.bot_status) === 2 && page.expires_at && item.date && new Date(item.date) > new Date(page.expires_at)) return 'expiring'
      // Assignée avec validation requise, non validée, et qui doit partir dans moins de 24 h : sera bloquée à l'envoi
      if (item.validation_pending && item.date && new Date(item.date).getTime() - Date.now() <= 24 * 3600 * 1000) return 'validation'
      return null
    },

    /**
     * Validation requise (affectedTo.optionValidation) et pas encore donnée : wait_validation est renseigné
     * une fois la publication validée (même règle que validationStatus du drawer)
     */
    isValidationPending(embed) {
      const affected = (embed && embed.affectedTo) || {}
      const required = affected.optionValidation === true || affected.optionValidation === 'true'
      const validated = embed && embed.wait_validation !== undefined && embed.wait_validation !== null && embed.wait_validation !== 'null' && embed.wait_validation !== ''
      return required && !validated
    },

    /**
     * Page sans connexion utilisable : ni API (bot_status 1/2/3), ni natif connecté.
     * Même règle que l'état des pages (OverviewPages.isDisconnected)
     */
    isPageUnusable(page) {
      if (!page) return false
      const stat = ((this.overview && this.overview.per_page) || []).find(p => p.id === page.id)
      const native = stat && stat.native
      const nativeOnly = !!((native && native.only) || (page.story_config && page.story_config.onlyConciergerie))
      if (!native) return Number(page.bot_status) === 0 && !nativeOnly
      const apiOk = !nativeOnly && [1, 2, 3].includes(Number(page.bot_status))
      return !apiOk && native.state !== 'connected'
    },

    // Reconnexion directe uniquement pour les pages API classiques ; conciergerie / Bluesky passent par la section pages
    canReconnectInline(page) {
      if (!page || ['bluesky', 'oneSignal', 'wonderpush', 'telegram', 'whatsapp'].includes(page.network)) return false
      if (page.isGroup && page.network === 'instagram') return false
      if (page.story_config && page.story_config.onlyConciergerie) return false
      return this.checkLock(page.network)
    },

    reconnect(page) {
      if (!this.canReconnectInline(page)) {
        this.go({ name: 'pages' })
        return
      }
      this.confirmRefresh(page)
    },

    /**
     * Point d'entrée unique pour ouvrir un post (dernières publications, à venir, top posts).
     * Ouvre le post dans Posts2 (mêmes infos que « Copier le lien » du drawer), passées en params
     * du router : rien dans l'URL, donc le post ne se rouvre pas au rechargement
     */
    openPublishedPost(post) {
      if (!post.news_id || !post.page_id || !post.bot_id) {
        this.openPost(post)
        return
      }
      localStorage.setItem('section', 'posts2')
      const params = { post_id: String(post.news_id), page_id: String(post.page_id), bot_id: String(post.bot_id) }
      if (post.is_draft) params.is_draft = true
      this.$router.push({ name: 'posts2', params })
    },

    /**
     * Ouvre le post sur le réseau, même logique que showOriginalPost du drawer
     */
    async openOnNetwork(post) {
      const id = post.network_post_id
      let url = null
      try {
        switch (post.network) {
          case 'facebook':
            url = `https://www.facebook.com/${id}`
            break
          case 'twitter':
            url = `https://www.twitter.com/${post.page_name}/status/${id}`
            break
          case 'linkedin':
            if (post.post_number_bis) {
              url = `https://www.linkedin.com/feed/update/${post.post_number_bis}`
            } else {
              const res = await this.getLinkedinPost(post.page_id, id)
              const body = (res && res.body) || {}
              if (body.success && body.post) url = `https://www.linkedin.com/feed/update/${body.post.activity || body.post.id}`
            }
            break
          case 'pinterest':
            url = `https://www.pinterest.fr/pin/${id}`
            break
          case 'google':
            if (post.post_number_bis) {
              url = post.post_number_bis
            } else {
              const res = await this.getGooglePost(post.page_id, id)
              const body = (res && res.body) || {}
              url = body.success && body.searchUrl ? body.searchUrl : `https://www.google.com/search?q=${id}`
            }
            break
          case 'youtube':
            url = `https://www.youtube.com/shorts/${id}`
            break
          case 'threads':
            url = `https://www.threads.net/@${post.page_name}`
            break
          case 'bluesky':
            url = `https://bsky.app/profile/${post.page_name}/post/${String(id).split('/').pop()}`
            break
        }
      } catch (e) {
        console.error('[overview] network link', e)
      }
      if (url) Popup.open(url, '', 900, 700)
    },

    // Actions par page : la page passe en params du router (rien dans l'URL, sélection enregistrée non écrasée)
    createPostForPage(page) {
      this.go({ name: 'postCreator', params: { pageId: String(page.id) } })
    },

    // Reporting sur la page et la période affichée ; « depuis le début » est ramené à 12 mois (calcul jour par jour)
    openReportingForPage(page) {
      if (!isReportingEligible(page)) return
      const { since, until } = periodRange(this.period)
      const end = until ? moment.unix(until) : moment()
      const start = since ? moment.unix(since) : moment().subtract(12, 'months')
      this.go({ name: 'reporting', params: { since: start.format('YYYY-MM-DD'), until: end.format('YYYY-MM-DD'), pageIds: String(page.id) } })
    },

    // Pages sans statistiques : ouvertes ensemble dans le Reporting (qui récupère leurs données) ; au retour l'overview est recalculé
    openReportingForPages(ids) {
      const { since, until } = periodRange(this.period)
      const end = until ? moment.unix(until) : moment()
      const start = since ? moment.unix(since) : moment().subtract(12, 'months')
      try { sessionStorage.setItem(REFRESH_AFTER_REPORTING, '1') } catch (e) { /* indisponible */ }
      this.go({ name: 'reporting', params: { since: start.format('YYYY-MM-DD'), until: end.format('YYYY-MM-DD'), pageIds: ids.join(',') } })
    },

    // Retour du Reporting après l'encart : calcul de l'overview relancé sans le cache de l'engine
    consumeReportingRefresh() {
      try {
        if (!sessionStorage.getItem(REFRESH_AFTER_REPORTING)) return false
        sessionStorage.removeItem(REFRESH_AFTER_REPORTING)
        return true
      } catch (e) {
        return false
      }
    },

    openInboxForPage(page) {
      this.go({ name: 'moderation', params: { moderation: String(page.id) } })
    },

    /**
     * 20 derniers événements en attente (statuts 0 et 2, comme le compteur) : l'API pagine par 10
     */
    async loadInboxEvents() {
      // Une seule mise à jour réseau par visite ; le cache éventuel reste affiché pendant ce temps
      if (this.inboxEventsLoading || this.inboxEventsFresh) return
      const pages = this.scopedPages.filter(p => p && p.id)
      if (!pages.length) return
      this.inboxEventsLoading = true
      try {
        const filter = { status: [0, 2] }
        // from en chaîne (« 0 » inclus) : sans from, l'engine relance une synchro TikTok, inutile ici
        const responses = await Promise.all(['0', '10'].map(from => this.listInboxEvents(pages, from, filter)))
        if (!this.alive) return
        responses.filter(res => !res || !res.ok).forEach(res => console.error('[overview] inbox', res && res.status))
        const events = responses.flatMap(res => (res && res.body && Array.isArray(res.body.events) ? res.body.events : []))
        const seen = new Set()
        this.inboxEvents = events
          .filter(e => e && e._id && !seen.has(e._id) && seen.add(e._id))
          .map(e => this.toInboxItem(e))
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 20)
        this.inboxEventsFresh = true
        writeSection(this.cacheKey(), 'inboxEvents', this.inboxEvents.map(({ page, ...item }) => item))
      } catch (e) {
        if (!this.inboxEvents) this.inboxEvents = []
      } finally {
        this.inboxEventsLoading = false
      }
    },

    // Événement Inbox -> ligne de pop-in ; contenu lu comme dans moderation/ListInbox.vue
    toInboxItem(e) {
      const data = e.data || {}
      const value = (data.changes && data.changes.value) || {}
      const user = e.user || {}
      const page = this.pagesData.find(p => String(p.network_id) === String(e.page) && p.network === e.network) || null
      const text = (data.comment && data.comment.text) ||
        value.message || value.text || value.review_text ||
        (data.messaging && data.messaging.message && data.messaging.message.text) ||
        (data.decoratedGeneratedActivity && data.decoratedGeneratedActivity.comment && data.decoratedGeneratedActivity.comment.text) ||
        (data.messages && data.messages.elements && data.messages.elements[0] && data.messages.elements[0].body && data.messages.elements[0].body.text) || ''
      const author = user.localizedName || user.name || user.username ||
        (value.from && (value.from.username || value.from.name)) ||
        (data.comment && data.comment.user && data.comment.user.name) || ''
      // date en secondes Unix (cf. ListInbox.formatDate)
      const timestamp = Number(e.date) < 1e12 ? Number(e.date) * 1000 : Number(e.date)
      const typeKey = `dash.inbox.type.${e.type}`
      return {
        key: e._id,
        object_id: e.object_id,
        page_id: page ? page.id : null,
        page,
        network: e.network,
        type: this.$te(typeKey) ? this.$t(typeKey) : e.type,
        raw_type: e.type,
        author: String(author),
        text: String(typeof text === 'string' ? text : '').replace(/<[^>]*>/g, '').slice(0, 160),
        timestamp: timestamp || 0
      }
    },

    openInboxEvent(item) {
      if (!item.page_id) {
        this.go({ name: 'moderation' })
        return
      }
      const params = { moderation: String(item.page_id) }
      if (item.object_id) params.object_id = String(item.object_id)
      this.go({ name: 'moderation', params })
    },

    openPost(item) {
      const date = item.date || item.posted_date
      this.go({ name: 'posts2', tab: 'planner', params: date ? { date: moment(date).toISOString(), scrollToHour: true } : {} })
    },

    go(target) {
      if (!target || !target.name) return
      if (target.tab) {
        try {
          localStorage.setItem('posts/tab', target.tab)
        } catch (e) {
          // stockage indisponible
        }
      }
      localStorage.setItem('section', target.name)
      this.$router.push({ name: target.name, params: target.params || {} })
    }
  }
}
